import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { ArrowUp } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Input } from '@voll-intelligence/ui-kit/ui/input';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import {
  CheckFilterRow,
  FilterSection,
  PriceRangeSection,
  ResultsFiltersPanel,
  type ResultsFiltersPanelHeader,
  SingleChoiceSection,
} from '../../shared/results_page/results_filters_panel.js';
import { AmenityGlyph } from '../details/hotel_details_amenities.js';
import type { HotelOfferView } from '../models.js';
import { matchesPolicy, type HotelPolicyFacts } from './hotel_policy_cut.js';
import { ToggleFilterSections } from './hotel_results_toggle_filters.js';
import './hotel_results_filters.css';

export type HotelFilterableOffer = HotelPolicyFacts & {
  offer: HotelOfferView;
  nightlyPrice?: number;
  rating?: number;
  distance?: number;
  /** Só a ordenação usa (`Reviews`): é o LASTRO da nota, não um eixo de corte —
   *  ninguém filtra "hotéis com mais de N avaliações". */
  reviewCount?: number;
  /** O hotel tem ALGUMA tarifa com café da manhã (item 6). Tri-estado de
   *  propósito: `true`/`false` são afirmação do fornecedor, `undefined` é
   *  desconhecido — e o toggle "Café da manhã" nem aparece se NENHUM offer souber
   *  (degrada só quando o agregado não veio, sem quebrar). */
  hasBreakfast?: boolean;
  /** O hotel tem ALGUMA tarifa reembolsável/cancelável (item 7). Mesmo
   *  tri-estado e mesma degradação de `hasBreakfast`. */
  hasRefundable?: boolean;
  /** Hotel com acordo da empresa (#6928). Só o `true` viaja no wire: ausente é
   *  "sem acordo", não desconhecido. */
  agreedRate?: boolean;
};

export type HotelResultsFilterValue = {
  readonly policyOnly: boolean;
  readonly breakfastOnly: boolean;
  readonly refundableOnly: boolean;
  /** Só hotéis com acordo (#6928): corta NO SERVIDOR (`onlyAgreedRate`).
   *  Opcional para quem monta o valor sem o eixo (diretivas do agente). */
  readonly agreedRateOnly?: boolean;
  /** Piso da diária — o par do teto desde o layout de faixa do protótipo
   *  (2026-08-25). Corta só LOCALMENTE (o servidor não conhece piso), como as
   *  comodidades já fazem. `undefined` = alça no mínimo do catálogo. */
  readonly minimumNightlyPrice?: number;
  readonly maximumNightlyPrice?: number;
  /** Pisos das FAIXAS de nota marcadas (`HOTEL_RATING_BAND_FLOORS`), em `or`:
   *  marcar duas faixas é "uma OU outra" (#2263) — categoria se combina, ao
   *  contrário das comodidades (requisitos, `and`). Vazio/`undefined` = eixo
   *  neutro. */
  readonly ratingBands?: readonly HotelRatingBandFloor[];
  readonly maximumDistance?: number;
  readonly amenityIds: readonly string[];
};

export type HotelResultsFiltersCopy = {
  filtersLabel: string;
  collapseFiltersLabel: string;
  /** O fechar da folha de tela cheia do palco estreito (`dismiss='close'`) —
   *  o X do cabeçalho, no lugar do recolher da coluna (#2663). */
  closeFiltersLabel: string;
  /** O botão do pé da folha de tela cheia (`HotelResultsFiltersSheet`): comete
   *  o rascunho e fecha. */
  applyFiltersLabel: string;
  resetFiltersLabel: string;
  policyLabel: string;
  withinPolicyLabel: string;
  /** Título da seção que agrupa os toggles de tarifa (café/reembolso). */
  rateLabel: string;
  breakfastLabel: string;
  refundableLabel: string;
  agreedRateLabel: string;
  nightlyPriceLabel: string;
  /** Nomes acessíveis das pontas editáveis da faixa de diária. */
  minimumPriceLabel: string;
  maximumPriceLabel: string;
  ratingLabel: string;
  distanceLabel: string;
  amenitiesLabel: string;
  commandPlaceholder: string;
  sendLabel: string;
  distanceUpToLabel(distance: number): string;
  ratingThresholdLabel(rating: number): string;
  /** Rótulo de faixa FECHADA de nota ("4,0 – 4,4"); a faixa do topo continua
   *  no `ratingThresholdLabel` ("4,5+"), que segue em uso. */
  ratingBandLabel(from: number, upTo: number): string;
};

export type HotelResultsFiltersProps = ResultsFiltersPanelHeader & {
  offers: readonly HotelFilterableOffer[];
  currency: string;
  value: HotelResultsFilterValue;
  copy: HotelResultsFiltersCopy;
  locked?: boolean;
  /** A casca de vidro (fundo, anel, sombra, blur). `false` só na página
   *  determinística do resultado, onde os filtros são uma coluna DA página —
   *  painel de vidro dentro de página lê como caixa dentro de caixa (a mesma
   *  prop `moldura` do protótipo). Na tela cheia (o padrão) a coluna flutua
   *  sobre o mapa e a moldura é o que a descola do tile. */
  frame?: boolean;
  /** As contagens vêm da lista CARREGADA e o fornecedor tem mais hotéis (o
   *  "N de M" do cabeçalho). Nos eixos que viram corte NO SERVIDOR (nota e
   *  distância) a contagem local mede a página, não o conjunto — então essas
   *  linhas saem SEM número e seguem clicáveis; o clique re-consulta o total
   *  (#3399 c). Comodidades cortam no cliente: mantêm a contagem e o zero
   *  continua inerte (racional do protótipo). */
  countsPartial?: boolean;
  onChange(value: HotelResultsFilterValue): void;
  onCommandSubmit?: (command: string) => void;
};

type FilterAxis =
  | 'policyOnly'
  | 'breakfastOnly'
  | 'refundableOnly'
  | 'agreedRateOnly'
  | 'minimumNightlyPrice'
  | 'maximumNightlyPrice'
  | 'ratingBands'
  | 'maximumDistance'
  | 'amenityIds';

/** Pisos das faixas de nota, do topo pro chão. Cada piso define a faixa
 *  MEIO-ABERTA [piso, piso de cima) — a do topo é aberta ([4,5, ∞)). Exportado
 *  como fonte única da geometria: o tradutor de cortes do painel (genui-kit,
 *  `toHotelResultsCuts`) deriva as faixas do fornecedor DAQUI, nunca de uma
 *  cópia — dois literais independentes não têm igualdade travável. */
export const HOTEL_RATING_BAND_FLOORS = [4.5, 4, 3.5] as const;
export type HotelRatingBandFloor = (typeof HOTEL_RATING_BAND_FLOORS)[number];

/** Limites da faixa de um piso: `to` é o piso de cima (EXCLUSIVO no corte de
 *  cliente; o tradutor de cortes decide o fechamento do lado do fornecedor),
 *  ausente na faixa do topo. */
export function hotelRatingBandBounds(floor: HotelRatingBandFloor): { from: number; to?: number } {
  const above = [...HOTEL_RATING_BAND_FLOORS].filter((candidate) => candidate > floor);
  return above.length === 0 ? { from: floor } : { from: floor, to: Math.min(...above) };
}
export const DISTANCE_THRESHOLDS = [1, 3, 5] as const;

export const EMPTY_HOTEL_RESULTS_FILTERS: HotelResultsFilterValue = {
  policyOnly: false,
  breakfastOnly: false,
  refundableOnly: false,
  amenityIds: [],
};

/** Eixo booleano de tarifa (café/reembolso). Lenient no desconhecido, igual ao
 *  filtro IRMÃO `matchesStayPreferences` (genui-kit `hotel_results_query.ts`):
 *  um toggle LIGADO exclui só quem o fornecedor NEGA (`false`); `undefined` (o
 *  agregado não veio) passa — "marca ausente não elimina", senão o filtro sumiria
 *  com oferta sem resume. No estado normal (agregado presente em todos) isso é
 *  idêntico a exigir `true`; só difere no `undefined` raro. É também o que impede
 *  a lista de ZERAR sem saída: se o agregado some de todos com o toggle ligado
 *  (e a seção então se esconde), nada é excluído em vez de esvaziar a lista. */
function matchesRateFlag(value: boolean | undefined, only: boolean, ignored: boolean): boolean {
  return ignored || !only || value !== false;
}

/** Teto genérico de eixo numérico: sem dado, a oferta SAI quando há teto ativo
 *  (um número desconhecido não prova que cabe na régua). */
function matchesMaximum(
  value: number | undefined,
  maximum: number | undefined,
  ignored: boolean,
): boolean {
  return ignored || maximum === undefined || (value !== undefined && value <= maximum);
}

/** Piso genérico de eixo numérico — o espelho de `matchesMaximum`. */
function matchesMinimum(
  value: number | undefined,
  minimum: number | undefined,
  ignored: boolean,
): boolean {
  return ignored || minimum === undefined || (value !== undefined && value >= minimum);
}

/** Faixas de nota em `or` (#2263): marcar "4,5+" e "4,0 – 4,4" pede uma OU
 *  outra — categoria combinada, não requisito somado. Sem dado, a oferta SAI
 *  quando há faixa ativa (mesma regra dos eixos numéricos: número desconhecido
 *  não prova que cabe). Faixa meio-aberta [piso, piso de cima): cada nota cai
 *  em exatamente UMA faixa, senão a soma das contagens passaria do total. */
function matchesRatingBands(
  rating: number | undefined,
  bands: readonly HotelRatingBandFloor[] | undefined,
  ignored: boolean,
): boolean {
  if (ignored || bands === undefined || bands.length === 0) return true;
  if (rating === undefined) return false;
  return bands.some((floor) => {
    const { from, to } = hotelRatingBandBounds(floor);
    return rating >= from && (to === undefined || rating < to);
  });
}

/** Comodidades em `and`: marcar duas exige as duas (quem marca dois requisitos
 *  não quer a união deles — regra do protótipo). */
function matchesAmenities(
  facts: HotelFilterableOffer,
  filters: HotelResultsFilterValue,
  ignoredAxis?: FilterAxis,
): boolean {
  return (
    ignoredAxis === 'amenityIds' ||
    filters.amenityIds.every((amenityId) =>
      facts.offer.amenities.some(({ id }) => id === amenityId),
    )
  );
}

/** O corte completo, puro — cada eixo é um `and`; `ignoredAxis` tira UM eixo da
 *  conta, que é o que faz a contagem de cada opção ser honesta (quantos sobram
 *  considerando os OUTROS cortes). */
export function matchesHotelResultsFilters(
  facts: HotelFilterableOffer,
  filters: HotelResultsFilterValue,
  ignoredAxis?: FilterAxis,
): boolean {
  return (
    matchesPolicy(facts, filters, ignoredAxis) &&
    matchesRateFlag(facts.hasBreakfast, filters.breakfastOnly, ignoredAxis === 'breakfastOnly') &&
    matchesRateFlag(
      facts.hasRefundable,
      filters.refundableOnly,
      ignoredAxis === 'refundableOnly',
    ) &&
    (ignoredAxis === 'agreedRateOnly' ||
      filters.agreedRateOnly !== true ||
      facts.agreedRate === true) &&
    matchesMinimum(
      facts.nightlyPrice,
      filters.minimumNightlyPrice,
      ignoredAxis === 'minimumNightlyPrice',
    ) &&
    matchesMaximum(
      facts.nightlyPrice,
      filters.maximumNightlyPrice,
      ignoredAxis === 'maximumNightlyPrice',
    ) &&
    matchesRatingBands(facts.rating, filters.ratingBands, ignoredAxis === 'ratingBands') &&
    matchesMaximum(facts.distance, filters.maximumDistance, ignoredAxis === 'maximumDistance') &&
    matchesAmenities(facts, filters, ignoredAxis)
  );
}

function ReplyComposer({
  copy,
  locked,
  onSubmit,
}: {
  readonly copy: HotelResultsFiltersCopy;
  readonly locked: boolean;
  readonly onSubmit: (command: string) => void;
}): ReactNode {
  const [command, setCommand] = useState('');
  const submit = () => {
    const text = command.trim();
    if (!text || locked) return;
    onSubmit(text);
    setCommand('');
  };
  return (
    <form
      className="hui-hotel-results-filter-reply"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Input
        value={command}
        placeholder={copy.commandPlaceholder}
        disabled={locked}
        onChange={(event) => {
          setCommand(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          submit();
        }}
      />
      <Button
        type="submit"
        size="icon-sm"
        className="rounded-full"
        aria-label={copy.sendLabel}
        disabled={locked}
      >
        <ArrowUp aria-hidden="true" />
      </Button>
    </form>
  );
}

/** Há corte ativo? É o que liga o `Redefinir todos` do cabeçalho. */
function hasActiveFilters(filters: HotelResultsFilterValue): boolean {
  return (
    filters.policyOnly ||
    filters.breakfastOnly ||
    filters.refundableOnly ||
    filters.agreedRateOnly === true ||
    filters.minimumNightlyPrice !== undefined ||
    filters.maximumNightlyPrice !== undefined ||
    (filters.ratingBands?.length ?? 0) > 0 ||
    filters.maximumDistance !== undefined ||
    filters.amenityIds.length > 0
  );
}

/** As réguas que saem do CATÁLOGO em cena: a faixa de diária (piso/teto do
 *  slider) e as comodidades que existem de fato, ordenadas pelo quanto
 *  aparecem — filtro que oferece opção inexistente devolve lista vazia sem
 *  explicar por quê. */
export function useFilterCatalog(offers: readonly HotelFilterableOffer[]) {
  const prices = useMemo(
    () => offers.flatMap(({ nightlyPrice }) => (nightlyPrice === undefined ? [] : [nightlyPrice])),
    [offers],
  );
  /* Ordenado pelo quanto APARECE, e sem teto — `amenitiesDoCatalogo` do
     protótipo. A ordem do fornecedor punha uma comodidade de um hotel só à
     frente da que quase todos têm, e o corte em oito escondia justamente as
     raras, que são as que mais separam a lista. */
  const amenities = useMemo(() => {
    const catalog = new Map<
      string,
      { amenity: HotelOfferView['amenities'][number]; count: number }
    >();
    offers.forEach(({ offer }) => {
      offer.amenities.forEach((amenity) => {
        const entry = catalog.get(amenity.id);
        if (entry) entry.count += 1;
        else catalog.set(amenity.id, { amenity, count: 1 });
      });
    });
    return [...catalog.values()].sort((a, b) => b.count - a.count).map(({ amenity }) => amenity);
  }, [offers]);
  return {
    priceFloor: prices.length === 0 ? undefined : Math.min(...prices),
    priceCeiling: prices.length === 0 ? undefined : Math.max(...prices),
    amenities,
  };
}

/** A faixa de diária do hotel sobre a faixa de preço da casca: só o mapeamento
 *  do par piso/teto do valor do filtro. */
function NightlyPriceSection({
  floor,
  ceiling,
  currency,
  value,
  copy,
  locked,
  onChange,
}: {
  readonly floor: number;
  readonly ceiling: number;
  readonly currency: string;
  readonly value: HotelResultsFilterValue;
  readonly copy: HotelResultsFiltersCopy;
  readonly locked?: boolean;
  readonly onChange: (value: HotelResultsFilterValue) => void;
}): ReactNode {
  return (
    <PriceRangeSection
      title={copy.nightlyPriceLabel}
      floor={floor}
      ceiling={ceiling}
      currency={currency}
      value={{
        ...(value.minimumNightlyPrice === undefined ? {} : { minimum: value.minimumNightlyPrice }),
        ...(value.maximumNightlyPrice === undefined ? {} : { maximum: value.maximumNightlyPrice }),
      }}
      minimumLabel={copy.minimumPriceLabel}
      maximumLabel={copy.maximumPriceLabel}
      locked={locked}
      onChange={({ minimum, maximum }) => {
        onChange({ ...value, minimumNightlyPrice: minimum, maximumNightlyPrice: maximum });
      }}
    />
  );
}

/** O corpo rolável da coluna: os blocos de corte na ordem do protótipo
 *  (política/tarifa → diária → nota → distância → comodidades), separados por
 *  `Separator`, cada um condicionado ao dado que o sustenta. */
function HotelResultsFiltersBody({
  offers,
  currency,
  value,
  copy,
  locked,
  countsPartial,
  onChange,
}: Pick<
  HotelResultsFiltersProps,
  'offers' | 'currency' | 'value' | 'copy' | 'locked' | 'countsPartial' | 'onChange'
>): ReactNode {
  const { priceFloor, priceCeiling, amenities } = useFilterCatalog(offers);
  const countMatching = (
    ignoredAxis: FilterAxis,
    matchesAxis: (facts: HotelFilterableOffer) => boolean,
  ): number =>
    offers.filter(
      (facts) => matchesHotelResultsFilters(facts, value, ignoredAxis) && matchesAxis(facts),
    ).length;
  /* #3399 (c): nota e distância cortam NO SERVIDOR (`toHotelResultsCuts`), sobre
     o conjunto inteiro. Com a lista PARCIAL a contagem local mede a página, não
     o conjunto — "Nota 4,5+ · 1" devolvia 17 ao ligar —, então a linha sai SEM
     número (e, sem número, nunca tranca em zero). Exaustiva, conta como sempre. */
  const countServerCut = countsPartial ? () => undefined : countMatching;

  return (
    <Fragment>
      <ToggleFilterSections
        offers={offers}
        value={value}
        copy={copy}
        locked={locked}
        onChange={onChange}
      />
      {priceFloor !== undefined && priceCeiling !== undefined && (
        <Fragment>
          <Separator />
          <NightlyPriceSection
            floor={priceFloor}
            ceiling={priceCeiling}
            currency={currency}
            value={value}
            copy={copy}
            locked={locked}
            onChange={onChange}
          />
        </Fragment>
      )}
      {offers.some(({ rating }) => rating !== undefined) && (
        <Fragment>
          <Separator />
          {/* Checklist, não rádio (#2263): faixas de nota se COMBINAM ("4,5+"
              junto de "4,0 – 4,4"), no padrão visual dos demais eixos marcáveis.
              Cada linha conta a PRÓPRIA faixa (disjuntas, meio-abertas), então as
              contagens somadas nunca passam do total da lista. */}
          <FilterSection title={copy.ratingLabel}>
            <div className="-mx-1 flex w-auto flex-col gap-0">
              {HOTEL_RATING_BAND_FLOORS.map((floor) => {
                const { from, to } = hotelRatingBandBounds(floor);
                const selected = value.ratingBands?.includes(floor) ?? false;
                return (
                  <CheckFilterRow
                    key={floor}
                    label={
                      to === undefined
                        ? copy.ratingThresholdLabel(from)
                        : // Teto EXIBIDO da faixa meio-aberta [4, 4,5) é "4,4" — uma casa
                          // abaixo do piso de cima, arredondado aqui para o rótulo não
                          // depender de a copy formatar artefato de float (4 − 0,1 ≠ 3,9).
                          copy.ratingBandLabel(from, Math.round((to - 0.1) * 10) / 10)
                    }
                    checked={selected}
                    count={countServerCut('ratingBands', ({ rating: candidateRating }) =>
                      matchesRatingBands(candidateRating, [floor], false),
                    )}
                    disabled={locked}
                    onToggle={() => {
                      const bands = value.ratingBands ?? [];
                      const next = selected
                        ? bands.filter((band) => band !== floor)
                        : [...bands, floor];
                      onChange({
                        ...value,
                        ratingBands: next.length === 0 ? undefined : next,
                      });
                    }}
                  />
                );
              })}
            </div>
          </FilterSection>
        </Fragment>
      )}
      {offers.some(({ distance }) => distance !== undefined) && (
        <Fragment>
          <Separator />
          <SingleChoiceSection
            title={copy.distanceLabel}
            options={DISTANCE_THRESHOLDS}
            current={value.maximumDistance}
            locked={locked}
            labelOf={(distance) => copy.distanceUpToLabel(distance)}
            countOf={(distance) =>
              countServerCut(
                'maximumDistance',
                ({ distance: candidateDistance }) =>
                  candidateDistance !== undefined && candidateDistance <= distance,
              )
            }
            onPick={(maximumDistance) => {
              onChange({ ...value, maximumDistance });
            }}
          />
        </Fragment>
      )}
      {amenities.length > 0 && (
        <Fragment>
          <Separator />
          <FilterSection title={copy.amenitiesLabel}>
            <div className="-mx-1 grid">
              {amenities.map(({ id, label, icon }) => {
                const active = value.amenityIds.includes(id);
                const amenityIds = active
                  ? value.amenityIds.filter((amenityId) => amenityId !== id)
                  : [...value.amenityIds, id];
                return (
                  <CheckFilterRow
                    key={id}
                    label={label}
                    glyph={<AmenityGlyph icon={icon} />}
                    checked={active}
                    count={countMatching('amenityIds', ({ offer }) =>
                      offer.amenities.some((amenity) => amenity.id === id),
                    )}
                    disabled={locked}
                    onToggle={() => {
                      onChange({ ...value, amenityIds });
                    }}
                  />
                );
              })}
            </div>
          </FilterSection>
        </Fragment>
      )}
    </Fragment>
  );
}

/**
 * A coluna de cortes do hotel — a anatomia dos cortes do aéreo, por decisão do
 * protótipo (`CortesDoHotel`): cabeçalho fixo (recolher + `Filtros` +
 * `Redefinir todos`, desabilitado sem corte), corpo com scroll próprio e o
 * composer de comando opcional no pé. `frame=false` é a variante de coluna DE
 * página (sem a casca de vidro da tela cheia).
 */
export function HotelResultsFilters({
  offers,
  currency,
  value,
  copy,
  locked = false,
  countsPartial = false,
  onChange,
  onCommandSubmit,
  // O que sobra é a casca do painel (`frame`, `header`, `dismiss`, o recolher):
  // repassado inteiro, os defaults são os do próprio painel.
  ...panel
}: Readonly<HotelResultsFiltersProps>): ReactNode {
  return (
    <ResultsFiltersPanel
      {...panel}
      copy={copy}
      active={hasActiveFilters(value)}
      locked={locked}
      onReset={() => {
        onChange(EMPTY_HOTEL_RESULTS_FILTERS);
      }}
      footer={
        onCommandSubmit && <ReplyComposer copy={copy} locked={locked} onSubmit={onCommandSubmit} />
      }
    >
      <HotelResultsFiltersBody
        offers={offers}
        currency={currency}
        value={value}
        copy={copy}
        locked={locked}
        countsPartial={countsPartial}
        onChange={onChange}
      />
    </ResultsFiltersPanel>
  );
}
